import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';

/** Basic wrapper around useState for storing [`Record<K,V>`](https://www.typescriptlang.org/docs/handbook/utility-types.html#recordkeys-type)
 * state which exposes an additional `setRecord` callback to change a single
 * item in the entire object
 *
 * @example
 * const [teamScores, setTeamScores, setTeamScore] = useRecordState({
 *     mercedes: 0,
 *     redBull: 0,
 * });
 *
 * // update a single item
 * setTeamScore('redBull', 1);
 *
 * // update all items
 * setTeamScore(prev => mapValues(score => score + 1));
 * */
export const useRecordState = <K extends string | number | symbol, V>(
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	initialValue = {} as Record<K, V>,
): readonly [Record<K, V>, Dispatch<SetStateAction<Record<K, V>>>, (key: K, value: V) => void] => {
	const [state, setState] = useState<Record<K, V>>(initialValue);

	const setItem = useCallback((key: K, value: V) => {
		setState((prev) => ({ ...prev, [key]: value }));
	}, []);

	return [state, setState, setItem] as const;
};
